import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from './ThemeToggle';

interface TopbarProps {
  rightSlot?: ReactNode;
  /**
   * Busca, entre a marca e as ações. Omitida, a marca fica sozinha.
   *
   * Existe pela mesma razão do `rightSlot`: só a vitrine tem chave do
   * Typesense, e o catch-all público não pode montar um campo que
   * consultaria um índice ao qual não têm acesso.
   */
  searchSlot?: ReactNode;
  /**
   * As ações do CELULAR. Quando presente, `rightSlot` + `ThemeToggle` só
   * aparecem a partir de `sm` — quem tem menu de celular assume a
   * responsabilidade de alcançar tudo o que eles alcançavam.
   *
   * Omitido (é o caso do catch-all público), a barra segue
   * exatamente como era em toda largura.
   */
  mobileSlot?: ReactNode;
}

/**
 * O padding horizontal fica no `<header>`, FORA do `max-w-6xl`, para casar com
 * o `PageShell` — que também o põe do lado de fora do container. Dentro dele,
 * o padding comeria 80px da largura útil no desktop e a barra do topo ficaria
 * visivelmente mais estreita que o conteúdo da página.
 *
 * A barra NÃO reage ao foco da busca. O scrim daquele estado começa abaixo
 * dela (`top-16`) justamente para não precisar animar nada aqui: apagar marca
 * e ações junto exigia transição no cabeçalho inteiro, e o movimento ficava
 * pior que o problema que resolvia.
 */
export function Topbar({ rightSlot, searchSlot, mobileSlot }: TopbarProps) {
  return (
    <header
      className="sticky top-0 z-40 border-b border-border bg-background/85 px-6 backdrop-blur supports-[backdrop-filter]:bg-background/70 md:px-10"
      role="banner"
    >
      {/* Duas linhas no celular, uma a partir de `sm`, e a altura é FIXA nos
          dois casos porque o scrim da busca se ancora nela (`top-28 sm:top-16`
          em StorefrontSearchAutocomplete).

          `h-28` = `py-2` (16px) + linha de 44px + `gap-y-2` (8px) + o campo de
          44px. Mexer em qualquer uma dessas medidas move o cabeçalho, e o
          scrim precisa ir junto. */}
      <div className="mx-auto flex h-28 w-full max-w-6xl flex-wrap items-center gap-x-3 gap-y-2 py-2 sm:h-16 sm:flex-nowrap sm:gap-4 sm:py-0">
        {/* Só a marca gráfica. O `aria-label` é o que dá nome ao link: sem o
            texto ao lado, o `Logo` sozinho não tem nome acessível. */}
        <Link
          to="/"
          className="order-1 flex h-11 shrink-0 items-center text-foreground sm:h-auto"
          aria-label="Mercado Único Clearance — voltar para a página inicial"
        >
          {/* A cor vai no próprio `Logo`, e não no `Link`: ele pinta com
              `currentColor`, então herdaria a cor do link e a levaria junto
              para qualquer texto que voltasse a acompanhá-lo. */}
          <Logo className="h-4 w-auto text-primary" />
        </Link>

        {/* `min-w-0` é o que deixa o campo encolher: sem ele o `flex-1` para de
            ceder na largura mínima do input e as ações da direita saem da tela
            no celular. */}
        {/* `order-3` joga a busca para a SEGUNDA linha no celular, com
            `w-full`; a partir de `sm` ela volta para o meio. É a mesma
            instância nos dois casos — renderizar duas montaria dois combobox,
            com estado e `aria` duplicados. */}
        {searchSlot && (
          <div className="order-3 w-full min-w-0 sm:order-2 sm:w-auto sm:flex-1">
            {searchSlot}
          </div>
        )}

        {/* `ml-auto` e não `justify-between` no pai: sem a busca montada, é ele
            que mantém as ações coladas na direita. */}
        <div className="order-2 ml-auto flex h-11 shrink-0 items-center gap-2 sm:order-3 sm:h-auto">
          <div
            className={
              mobileSlot
                ? 'hidden items-center gap-2 sm:flex'
                : 'flex items-center gap-2'
            }
          >
            {rightSlot}
            <ThemeToggle />
          </div>
          {mobileSlot && (
            <div className="flex items-center gap-2 sm:hidden">{mobileSlot}</div>
          )}
        </div>
      </div>
    </header>
  );
}
